import React, { useState, useRef, useLayoutEffect } from "react";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import OndemandVideoOutlinedIcon from "@mui/icons-material/OndemandVideoOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { IconButton } from "@mui/material";

const API = "https://hr-backend-72v1.onrender.com";
export const mediaUrl = (fileId) =>
  `${API}/project-showcase/media/${fileId}/download`;
const isImage = (m) => (m.mimeType || "").startsWith("image/");

// A showcase link may have been saved with a scheme-less URL ("loom.com/…") or
// with the url/label fields swapped (URL pasted into "label"). Resolve the real
// destination + a human label so the link ALWAYS opens externally in a new tab
// and is copyable — never a relative link that reopens the HRMS app.
const looksLikeUrl = (s = "") =>
  /^https?:\/\//i.test(s.trim()) || /^[a-z0-9-]+(\.[a-z0-9-]+)+/i.test(s.trim());
const withScheme = (s = "") => {
  const t = s.trim();
  return /^https?:\/\//i.test(t) ? t : `https://${t}`;
};
export const resolveLink = (l = {}) => {
  const url = (l.url || "").trim();
  const label = (l.label || "").trim();
  const href = looksLikeUrl(url)
    ? withScheme(url)
    : looksLikeUrl(label)
    ? withScheme(label)
    : null;
  const text =
    !looksLikeUrl(label) && label
      ? label
      : !looksLikeUrl(url) && url
      ? url
      : "Watch";
  return { href, text };
};

const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : null;

// Read-only card used by both the browse view and the manager's own list.
// Cards share a uniform collapsed height; "Read more" expands the card to show
// the full description plus media, links and team members. Owner action buttons
// (edit/delete) show when onEdit/onDelete are provided.
const ShowcaseCard = ({ entry, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const cardRef = useRef(null);
  const bodyRef = useRef(null);
  const [clamped, setClamped] = useState(false);

  // Detect whether the collapsed description overflows (so we only offer
  // "Read more" when there's actually more to reveal). Re-check on resize.
  useLayoutEffect(() => {
    const measure = () => {
      const el = bodyRef.current;
      if (el && !expanded) setClamped(el.scrollHeight > el.clientHeight + 4);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [entry.description, expanded]);

  // Masonry: size this card's grid row-span from its own measured height so the
  // .psGrid packs tightly — a shorter card slots up under a taller/expanded one
  // instead of leaving a big empty gap. ROW/GAP must match .psGrid's
  // grid-auto-rows (8px) and the vertical gap we want between stacked cards.
  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const ROW = 8;
    const GAP = 18;
    let last = 0;
    const apply = () => {
      // align-items:start keeps the card's height at its content height,
      // independent of the row-span we set — so this can't feed back into itself.
      const span = Math.max(1, Math.ceil((el.offsetHeight + GAP) / ROW));
      if (span !== last) {
        last = span;
        el.style.gridRowEnd = `span ${span}`;
      }
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [expanded, entry]);

  const start = formatDate(entry.startDate);
  const end = formatDate(entry.endDate);
  const dateLabel = start && end ? `${start} – ${end}` : start || end;

  const hasExtras =
    (entry.media && entry.media.length > 0) ||
    (entry.mediaLinks && entry.mediaLinks.length > 0) ||
    (entry.teamMembers && entry.teamMembers.length > 0);
  const hasMore = clamped || hasExtras;

  return (
    <article
      ref={cardRef}
      className={`psCard ${expanded ? "psCard--expanded" : ""}`}
    >
      <div className="psCardHead">
        <div>
          <h3 className="psCardTitle">{entry.title}</h3>
        </div>
        {(onEdit || onDelete) && (
          <span className="psCardActions">
            {onEdit && (
              <IconButton size="small" onClick={() => onEdit(entry)} title="Edit">
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            )}
            {onDelete && (
              <IconButton
                size="small"
                color="error"
                onClick={() => onDelete(entry)}
                title="Delete"
              >
                <DeleteOutlineOutlinedIcon fontSize="small" />
              </IconButton>
            )}
          </span>
        )}
      </div>

      <div className="psCardMeta">
        {entry.clientName && (
          <span>
            <BusinessOutlinedIcon />
            {entry.clientName}
          </span>
        )}
        {dateLabel && (
          <span>
            <CalendarMonthOutlinedIcon />
            {dateLabel}
          </span>
        )}
        {entry.ownerName && (
          <span>
            <PersonOutlineIcon />
            {entry.ownerName}
          </span>
        )}
      </div>

      {entry.services && entry.services.length > 0 && (
        <div className="psTags">
          {entry.services.map((s, i) => (
            <span className="psTag" key={i}>
              {s}
            </span>
          ))}
        </div>
      )}

      {/* Body: collapsed shows a clamped description; expanded reveals it all. */}
      <div
        className={`psCardBody ${!expanded && clamped ? "psCardBody--fade" : ""}`}
        ref={bodyRef}
      >
        {entry.description && <p className="psDesc">{entry.description}</p>}

        {expanded && entry.media && entry.media.length > 0 && (
          <div className="psMedia">
            {entry.media.map((m, i) =>
              isImage(m) ? (
                <a
                  className="psThumb"
                  key={i}
                  href={mediaUrl(m.fileId)}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={m.fileName}
                >
                  <img src={mediaUrl(m.fileId)} alt={m.fileName} />
                </a>
              ) : (
                <a
                  className="psThumbFile"
                  key={i}
                  href={mediaUrl(m.fileId)}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={m.fileName}
                >
                  <InsertDriveFileOutlinedIcon />
                  <span className="psThumbFileName">{m.fileName}</span>
                </a>
              )
            )}
          </div>
        )}

        {expanded && entry.mediaLinks && entry.mediaLinks.length > 0 && (
          <div className="psLinks">
            {entry.mediaLinks.map((l, i) => {
              const { href, text } = resolveLink(l);
              return href ? (
                <a
                  className="psLinkBtn"
                  key={i}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <OndemandVideoOutlinedIcon />
                  <span className="psLinkLabel">{text}</span>
                  <OpenInNewOutlinedIcon />
                </a>
              ) : (
                <span className="psLinkBtn" key={i}>
                  <OndemandVideoOutlinedIcon />
                  <span className="psLinkLabel">{text}</span>
                </span>
              );
            })}
          </div>
        )}

        {expanded && entry.teamMembers && entry.teamMembers.length > 0 && (
          <>
            <div className="psDivider" />
            <div className="psMembers">
              {entry.teamMembers.map((t, i) => (
                <span className="psMember" key={i}>
                  <GroupsOutlinedIcon />
                  {t.name || t.email}
                </span>
              ))}
            </div>
          </>
        )}
      </div>

      {hasMore && (
        <button
          type="button"
          className="psReadMore"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "Show less" : "Read more"}
        </button>
      )}
    </article>
  );
};

export default ShowcaseCard;
